import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Check, ClipboardList, Plus, Trash2 } from "lucide-react";
import { CopyMeasuresDialog, copyMeasures } from "@/components/modelsheets/CopyMeasures";
import { ModelFields } from "@/components/modelsheets/GeneralTab";
import { MEASURE_PRESETS, blankFabric } from "@/lib/modelSheets";
import { MaterialPicker } from "@/components/modelsheets/MaterialPicker";
import { isZip } from "@/lib/zips";

const Section = ({ title, action, children }) => (
  <section>
    <div className="mb-2 flex items-center justify-between gap-2">
      <h4 className="font-display text-sm font-semibold">{title}</h4>{action}
    </div>
    {children}
  </section>
);

const AddBtn = ({ onClick, label, testid }) => (
  <Button size="sm" variant="outline" onClick={onClick} data-testid={testid}><Plus className="mr-1 h-3.5 w-3.5" />{label}</Button>
);

const FabricRows = ({ rows, onChange, types }) => {
  const setType = (i) => (v) => onChange(rows.map((x, j) => (j === i ? { ...x, fabric_type: v } : x)));
  const items = types.map((t) => ({ name: t }));
  return (
    <div className="space-y-2">
      {rows.map((f, i) => (
        <div key={i} className={`grid grid-cols-[1fr_32px] items-center gap-2 rounded-md p-1 ${i % 2 ? "bg-secondary/60" : ""}`} data-testid={`tech-fabric-${i}`}>
          <MaterialPicker items={items} kind="tipo" value={f.fabric_type} onChange={setType(i)} onPick={(m) => setType(i)(m.name)} placeholder="Tipo di tessuto (es. Crepe, Fodera)" testid={`tech-fabric-${i}-type`} />
          <Button variant="ghost" size="icon" className="h-9 w-8 text-muted-foreground" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`tech-fabric-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      {rows.length === 0 && <p className="text-xs text-muted-foreground">Nessun tessuto: aggiungi quelli che servono per il capo (principale, fodera, contrasto...).</p>}
    </div>
  );
};

const MeasuresTable = ({ sizes, rows, onChange, baseSize }) => {
  const upd = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setVal = (i, r, z, v) => upd(i, { values: { ...r.values, [z]: v }, copied: { ...r.copied, [z]: false } });
  const pending = (r) => sizes.filter((z) => r.copied?.[z]).length;
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm" data-testid="tech-measures">
        <thead><tr className="border-b border-border text-xs text-muted-foreground">
          <th className="p-2 text-left font-medium">Punto di misura</th>
          {sizes.map((z) => <th key={z} className={`w-20 p-2 font-medium ${z === baseSize ? "text-primary" : ""}`}>{z}{z === baseSize && <span className="block text-[9px] uppercase">base</span>}</th>)}<th className="w-16" />
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={`border-b border-border/50 last:border-0 ${i % 2 ? "bg-secondary/60" : ""}`}>
              <td className="p-1"><Input value={r.point} onChange={(e) => upd(i, { point: e.target.value })} placeholder="es. Circonferenza seno" className="h-8 min-w-[160px]" data-testid={`tech-measure-${i}-point`} /></td>
              {sizes.map((z) => (
                <td key={z} className="p-1">
                  <Input value={r.values?.[z] ?? ""} onChange={(e) => setVal(i, r, z, e.target.value)} title={r.copied?.[z] ? "Copiata da un altro modello: controllala" : undefined}
                    className={`h-8 text-center ${r.copied?.[z] ? "border-amber-500 bg-amber-500/15 font-semibold text-amber-300" : ""}`} data-testid={`tech-measure-${i}-${z}`} />
                </td>
              ))}
              <td className="whitespace-nowrap p-1">
                {pending(r) > 0 && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-400" title="Le misure di questa riga vanno bene" onClick={() => upd(i, { copied: {} })} data-testid={`tech-measure-${i}-confirm`}><Check className="h-3.5 w-3.5" /></Button>
                )}
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PendingBanner = ({ rows, sizes }) => {
  const n = rows.reduce((t, r) => t + sizes.filter((z) => r.copied?.[z]).length, 0);
  if (!n) return null;
  return (
    <p className="mb-2 flex items-center gap-1.5 rounded bg-amber-500/10 px-2 py-1.5 text-xs text-amber-400" data-testid="tech-measures-pending">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />{n} {n === 1 ? "misura copiata da controllare" : "misure copiate da controllare"}: cambia il valore o conferma la riga con la spunta.
    </p>
  );
};

// Cerniera: lunghezza per taglia, così il lotto calcola quante zip di ogni misura servono
const ZipLengths = ({ a, sizes, measures, onChange, i }) => {
  const zipMeasure = measures.find((r) => /zip|cernier/i.test(r.point || ""));
  const set = (z, v) => onChange({ ...(a.lengths || {}), [z]: v });
  const fromMeasures = () => onChange(Object.fromEntries(sizes.map((z) => [z, zipMeasure.values?.[z] || ""])));
  return (
    <div className="col-span-3 flex flex-wrap items-center gap-2 pl-1 text-xs" data-testid={`tech-accessory-${i}-lengths`}>
      <span className="text-muted-foreground">Lunghezza per taglia (cm):</span>
      {sizes.map((z) => (
        <label key={z} className="flex items-center gap-1"><span className="font-medium">{z}</span>
          <Input value={a.lengths?.[z] ?? ""} onChange={(e) => set(z, e.target.value)} inputMode="decimal" className="h-7 w-14 text-center" data-testid={`tech-accessory-${i}-len-${z}`} /></label>
      ))}
      {zipMeasure && <Button type="button" size="sm" variant="ghost" className="h-7 text-primary" onClick={fromMeasures} data-testid={`tech-accessory-${i}-len-from-measures`}>Prendi da “{zipMeasure.point}”</Button>}
    </div>
  );
};

const AccessoryRows = ({ rows, onChange, items, sizes, measures }) => {
  const upd = (i, k) => (e) => onChange(rows.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  const setName = (i) => (v) => onChange(rows.map((x, j) => (j === i ? { ...x, name: v } : x)));
  const pick = (i) => (m) => onChange(rows.map((x, j) => (j === i ? { ...x, name: m.name } : x)));
  return (
    <div className="space-y-2">
      {rows.map((a, i) => (
        <div key={i} className={`grid grid-cols-[1fr_110px_32px] gap-2 rounded-md p-1 ${i % 2 ? "bg-secondary/60" : ""}`} data-testid={`tech-accessory-${i}`}>
          <MaterialPicker items={items} kind="accessorio" value={a.name} onChange={setName(i)} onPick={pick(i)} placeholder="es. Cerniera invisibile 50 cm" testid={`tech-accessory-${i}-name`} />
          <Input value={a.quantity} onChange={upd(i, "quantity")} placeholder="Quantità" data-testid={`tech-accessory-${i}-qty`} />
          <Button variant="ghost" size="icon" className="h-9 w-8 text-muted-foreground" onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
          {isZip(items.find((m) => m.name.toLowerCase() === (a.name || "").trim().toLowerCase()) || a) && sizes.length > 0 && (
            <ZipLengths a={a} i={i} sizes={sizes} measures={measures} onChange={(lengths) => onChange(rows.map((x, j) => (j === i ? { ...x, lengths } : x)))} />
          )}
        </div>
      ))}
      {rows.length === 0 && <p className="text-xs text-muted-foreground">Nessun accessorio: bottoni, cerniere, etichette, fodere...</p>}
    </div>
  );
};

const PresetButtons = ({ rows, onChange }) => {
  const load = (k) => {
    if (rows.some((r) => r.point?.trim() || Object.values(r.values || {}).some((v) => v)) && !window.confirm("Sostituire le misure attuali con quelle predefinite?")) return;
    onChange(MEASURE_PRESETS[k].map((point) => ({ point, values: {} })));
  };
  return (
    <div className="flex gap-1 rounded-md border border-border p-0.5">
      <Button size="sm" variant="ghost" className="h-7" onClick={() => load("sopra")} data-testid="tech-preset-sopra">Capo sopra</Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={() => load("sotto")} data-testid="tech-preset-sotto">Capo sotto</Button>
    </div>
  );
};

export const TechTab = ({ s, set, opts, ensureSaved, onImage }) => {
  const tech = s.tech;
  const setTech = (patch) => set({ ...s, tech: { ...tech, ...patch } });
  return (
    <div className="space-y-7 py-4">
      <datalist id="ms-fabric-types">{(opts.fabric_types || []).map((x) => <option key={x} value={x} />)}</datalist>
      <datalist id="ms-fabrics">{(opts.fabrics || []).map((x) => <option key={x} value={x} />)}</datalist>
      <datalist id="ms-accessory-list">{(opts.accessories || []).map((x) => <option key={x} value={x} />)}</datalist>
      <ModelFields s={s} set={set} ensureSaved={ensureSaved} onImage={onImage} />
      <Section title="Tabella misure per taglia (cm)" action={<div className="flex flex-wrap justify-end gap-2"><PresetButtons rows={tech.measures} onChange={(measures) => setTech({ measures })} /><CopyMeasuresDialog currentId={s.id} onCopy={(src) => setTech({ measures: copyMeasures(src.tech.measures.filter((m) => m.point), s.sizes) })} /><AddBtn label="Aggiungi misura" testid="tech-add-measure" onClick={() => setTech({ measures: [...tech.measures, { point: "", values: {} }] })} /></div>}>
        <PendingBanner rows={tech.measures} sizes={s.sizes} />
        {s.sizes.length ? <MeasuresTable sizes={s.sizes} baseSize={s.base_size} rows={tech.measures} onChange={(measures) => setTech({ measures })} /> : <p className="text-xs text-muted-foreground">Scegli prima le taglie del modello.</p>}
      </Section>
      <Section title="Tessuti per produrre il capo" action={<AddBtn label="Aggiungi tessuto" testid="tech-add-fabric" onClick={() => setTech({ fabrics: [...tech.fabrics, blankFabric()] })} />}>
        <FabricRows rows={tech.fabrics} types={opts.fabric_types || []} onChange={(fabrics) => setTech({ fabrics })} />
      </Section>
      <Section title="Accessori e materiali" action={<AddBtn label="Aggiungi accessorio" testid="tech-add-accessory" onClick={() => setTech({ accessories: [...tech.accessories, { name: "", quantity: "", notes: "" }] })} />}>
        <AccessoryRows rows={tech.accessories} items={opts.accessory_items || []} sizes={s.sizes || []} measures={tech.measures || []} onChange={(accessories) => setTech({ accessories })} />
      </Section>
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-4">
        <p className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-primary"><ClipboardList className="h-4 w-4" />Note per il laboratorio</p>
        <Textarea rows={7} value={s.notes} onChange={(e) => set({ ...s, notes: e.target.value })} placeholder="Cuciture, rifiniture, lavorazioni, sequenza di montaggio, attenzioni particolari, controlli da fare..." className="bg-background" data-testid="tech-lab-notes" />
        <div className="mt-3 rounded-md border-2 border-rose-500 bg-rose-500/10 p-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-rose-400"><AlertTriangle className="h-4 w-4" />Attenzione</p>
          <Textarea rows={3} value={s.attention || ""} onChange={(e) => set({ ...s, attention: e.target.value })} placeholder="Le cose a cui il laboratorio deve fare molta attenzione: verranno stampate in grande e ben in evidenza." className="border-rose-500/50 bg-background font-semibold" data-testid="tech-attention" />
        </div>
      </div>
    </div>
  );
};
